import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Star, ExternalLink } from 'lucide-react';
import { getNews, resolveImg } from '../lib/api';
import { useSettings } from '../lib/SettingsContext';

import { useLang } from '../lib/i18n';
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function Noticias() {
  const { ui } = useLang();
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getNews().then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const heroImg = resolveImg(settings.hero_images?.[1] || settings.hero_images?.[0] || '');
  const featured = items.find((n) => n.featured);
  const rest = items.filter((n) => !n.featured || n.id !== featured?.id);

  return (
    <div>
      <section className="relative h-[55vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 gd-hero-bg" style={{ backgroundImage: `url(${heroImg})` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,13,14,0.6), rgba(13,13,14,0.95))' }} />
        <div className="relative z-10 text-center px-6">
          <div className="gd-eyebrow mb-3">{ui('news.eyebrow')}</div>
          <h1 className="font-script text-[#c9a063] text-5xl md:text-7xl leading-none">{ui('news.title')}</h1>
          <p className="font-serif-display text-white text-2xl md:text-3xl mt-4">{ui('news.sub')}</p>
        </div>
      </section>

      <section className="py-20 px-6 md:px-10 bg-[#0d0d0e]">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-white/40 text-center py-20">{ui('news.loading')}</div>
          ) : items.length === 0 ? (
            <div className="text-white/40 text-center py-20">{ui('news.empty')}</div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Link to={`/noticias/${featured.slug || featured.id}`} className="gd-card group block mb-12 overflow-hidden md:grid md:grid-cols-5">
                  <div className="relative md:col-span-3 aspect-[16/9] md:aspect-auto bg-[#1a1a1d] overflow-hidden">
                    {featured.image ? <img src={resolveImg(featured.image)} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : null}
                    <div className="absolute top-4 left-4 px-3 py-1 bg-[#c9a063] text-black text-[10px] uppercase tracking-widest flex items-center gap-1.5"><Star size={11} /> {ui('news.featured')}</div>
                  </div>
                  <div className="md:col-span-2 p-7 md:p-10 flex flex-col justify-center">
                    <div className="text-[10px] tracking-[0.3em] text-[#c9a063] uppercase mb-3 flex items-center gap-2"><Calendar size={12} /> {formatDate(featured.date)}</div>
                    <h2 className="font-serif-display text-2xl md:text-3xl text-white leading-tight mb-3 group-hover:text-[#c9a063] transition-colors">{featured.title}</h2>
                    <div className="text-white/55 text-[15px] line-clamp-3" dangerouslySetInnerHTML={{ __html: featured.content || '' }} />
                    <span className="mt-5 text-[11px] uppercase tracking-[0.25em] text-[#c9a063] inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">{ui('btn.read_more')} <ArrowRight size={12} /></span>
                  </div>
                </Link>
              )}

              {/* Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((n) => (
                  <Link to={`/noticias/${n.slug || n.id}`} key={n.id} className="gd-card group overflow-hidden flex flex-col">
                    <div className="relative aspect-[16/9] overflow-hidden bg-[#1a1a1d]">
                      {n.image && <img src={resolveImg(n.image)} alt={n.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                    </div>
                    <div className="p-5 flex flex-col gap-2 flex-1">
                      <div className="text-[10px] tracking-[0.3em] text-[#c9a063] uppercase flex items-center gap-2"><Calendar size={11} /> {formatDate(n.date)}</div>
                      <h3 className="font-serif-display text-lg text-white leading-tight group-hover:text-[#c9a063] transition-colors">{n.title}</h3>
                      <div className="text-white/55 text-sm line-clamp-2 flex-1" dangerouslySetInnerHTML={{ __html: n.content || '' }} />
                      <span className="mt-2 text-[11px] uppercase tracking-[0.25em] text-[#c9a063] inline-flex items-center gap-1.5">{ui('btn.read')} <ArrowRight size={11} /></span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
